import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // Send HttpOnly cookies with requests
});

// In-memory token store (not localStorage — XSS-safe)
let _accessToken: string | null = localStorage.getItem('token');

export function setAccessToken(token: string | null) {
  _accessToken = token;
  // Keep localStorage in sync for backward compat during migration
  if (token) localStorage.setItem('token', token);
  else localStorage.removeItem('token');
}

export function getAccessToken(): string | null {
  return _accessToken;
}

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// Track refresh to avoid concurrent refresh calls
let _refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
    const newToken = res.data?.access_token;
    if (newToken) {
      setAccessToken(newToken);
      return newToken;
    }
  } catch {
    // Refresh failed — session expired
  }
  return null;
}

// On 401, try refresh once before redirecting
api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const originalRequest = err.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (
      err.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login') &&
      !window.location.pathname.startsWith('/portal')
    ) {
      originalRequest._retry = true;

      // Deduplicate concurrent refresh calls
      if (!_refreshPromise) {
        _refreshPromise = refreshAccessToken().finally(() => { _refreshPromise = null; });
      }
      const newToken = await _refreshPromise;

      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      // Refresh failed — clear and redirect
      setAccessToken(null);
      window.location.replace('/login');
    }
    return Promise.reject(err);
  }
);

// --- Auth ---
export const authApi = {
  register: (data: { email: string; password: string; first_name: string; last_name: string; consent_terms?: boolean; consent_privacy?: boolean; consent_newsletter?: boolean }) =>
    api.post('/auth/register', data),
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  requestCode: (email: string) =>
    api.post('/auth/request-code', { email }),
  verifyCode: (email: string, code: string) =>
    api.post('/auth/verify-code', { email, code }),
  me: () => api.get('/auth/me'),
  updateProfile: (data: any) => api.put('/auth/profile', data),
  testEmail: () => api.post('/auth/test-email'),
  getSignature: () => api.get('/auth/signature'),
  saveSignature: (signature: string) => api.put('/auth/signature', { signature }),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, new_password: string) => api.post('/auth/reset-password', { token, new_password }),
  // 2FA / TOTP
  loginTotp: (email: string, password: string, totp_code: string) =>
    api.post('/auth/login-totp', { email, password, totp_code }),
  totpSetup: () => api.post('/auth/totp/setup'),
  totpVerifySetup: (code: string) => api.post('/auth/totp/verify-setup', { code }),
  totpDisable: (email: string, password: string) => api.post('/auth/totp/disable', { email, password }),
  totpBackupCodes: (code: string) => api.post('/auth/totp/backup-codes', { code }),
  mfaEnableEmail: () => api.post('/auth/mfa/enable-email'),
  mfaResendCode: (email: string, password: string) => api.post('/auth/mfa/resend-code', { email, password }),
  refresh: () => api.post('/auth/refresh'),
  logout: () => api.post('/auth/logout'),
};

// --- Clients ---
export const clientsApi = {
  list: (params?: { status?: string; search?: string; skip?: number; limit?: number }) =>
    api.get('/clients', { params }),
  get: (id: string) => api.get(`/clients/${id}`),
  create: (data: any) => api.post('/clients', data),
  update: (id: string, data: any) => api.put(`/clients/${id}`, data),
  delete: (id: string) => api.delete(`/clients/${id}`),
  exportCsv: () => api.get('/clients/export/csv', { responseType: 'blob' }),
  exportPdf: () => api.get('/clients/export/pdf', { responseType: 'blob' }),
};

// --- Cases ---
export const casesApi = {
  list: (params?: { status?: string; case_type?: string; client_id?: string }) =>
    api.get('/cases', { params }),
  get: (id: string) => api.get(`/cases/${id}`),
  create: (data: any) => api.post('/cases', data),
  update: (id: string, data: any) => api.put(`/cases/${id}`, data),
  delete: (id: string) => api.delete(`/cases/${id}`),
};

// --- Sessions ---
export const sessionsApi = {
  list: (params?: { case_id?: string; client_id?: string; status?: string; skip?: number; limit?: number }) =>
    api.get('/sessions', { params }),
  get: (id: string) => api.get(`/sessions/${id}`),
  create: (data: any) => api.post('/sessions', data),
  update: (id: string, data: any) => api.put(`/sessions/${id}`, data),
  delete: (id: string) => api.delete(`/sessions/${id}`),
  cancel: (id: string) => api.post(`/sessions/${id}/cancel`),
  start: (id: string) => api.post(`/sessions/${id}/start`),
  end: (id: string) => api.post(`/sessions/${id}/end`),
  createZoomMeeting: (id: string) => api.post(`/sessions/${id}/create-zoom-meeting`),
  deleteZoomMeeting: (id: string) => api.delete(`/sessions/${id}/zoom-meeting`),
  // Status-Workflow
  complete: (id: string) => api.post(`/sessions/${id}/complete`),
  noShow: (id: string) => api.post(`/sessions/${id}/no-show`),
  // Rechnungs-Anbindung
  unbilled: (params?: { client_id?: string }) => api.get('/sessions/unbilled', { params }),
  linkInvoice: (data: { session_ids: string[]; invoice_id: string }) =>
    api.post('/sessions/link-invoice', data),
  // Session Notes
  saveDraft: (sessionId: string, notesDraft: any) =>
    api.post(`/sessions/${sessionId}/notes/draft`, { notes_draft: notesDraft }),
  getDraft: (sessionId: string) =>
    api.get(`/sessions/${sessionId}/notes/draft`),
  exportNotesPdf: (sessionId: string) =>
    api.get(`/sessions/${sessionId}/notes/pdf`, { responseType: 'blob' }),
  // Protokolle
  getProtocol: (sessionId: string) =>
    api.get(`/sessions/${sessionId}/protocol`),
  createProtocol: (sessionId: string, data: { protocol_content: string; key_points?: string; protocol_format?: string }) =>
    api.post(`/sessions/${sessionId}/protocol`, data),
  updateProtocol: (sessionId: string, protocolId: string, data: { protocol_content?: string; key_points?: string }) =>
    api.put(`/sessions/${sessionId}/protocol/${protocolId}`, data),
  sendProtocol: (sessionId: string, protocolId: string) =>
    api.post(`/sessions/${sessionId}/protocol/${protocolId}/send`),
};

// --- Session Note Templates ---
export const noteTemplatesApi = {
  list: () => api.get('/note-templates'),
  create: (data: { name: string; description?: string; structure: any; is_default?: boolean }) =>
    api.post('/note-templates', data),
  update: (id: string, data: { name?: string; description?: string; structure?: any; is_default?: boolean }) =>
    api.put(`/note-templates/${id}`, data),
  delete: (id: string) => api.delete(`/note-templates/${id}`),
};

// --- Notes Timeline ---
export const notesTimelineApi = {
  getClientTimeline: (clientId: string, params?: { skip?: number; limit?: number }) =>
    api.get(`/clients/${clientId}/notes-timeline`, { params }),
};

// --- Packages ---
export const packagesApi = {
  list: (params?: { category?: string; tier?: string; active_only?: boolean }) =>
    api.get('/packages', { params }),
  create: (data: any) => api.post('/packages', data),
  update: (id: string, data: any) => api.put(`/packages/${id}`, data),
  delete: (id: string) => api.delete(`/packages/${id}`),
  duplicate: (id: string) => api.post(`/packages/${id}/duplicate`),
  reorder: (id: string, newSortOrder: number) =>
    api.put(`/packages/${id}/reorder`, null, { params: { new_sort_order: newSortOrder } }),
  seedDefaults: () => api.post('/packages/seed/defaults'),
  clientPackages: (params?: { client_id?: string; status?: string }) =>
    api.get('/packages/client-packages', { params }),
  assignPackage: (data: any) => api.post('/packages/client-packages', data),
  useCredit: (cpId: string, sessionId?: string) =>
    api.post(`/packages/client-packages/${cpId}/use`, null, { params: sessionId ? { session_id: sessionId } : undefined }),
  restoreCredit: (cpId: string) =>
    api.post(`/packages/client-packages/${cpId}/restore`),
  activeForClient: (clientId: string) =>
    api.get(`/packages/clients/${clientId}/active`),
  pausePackage: (cpId: string) =>
    api.post(`/packages/client-packages/${cpId}/pause`),
  resumePackage: (cpId: string) =>
    api.post(`/packages/client-packages/${cpId}/resume`),
  cancelPackage: (cpId: string) =>
    api.post(`/packages/client-packages/${cpId}/cancel`),
};

// --- Methods ---
export const methodsApi = {
  categories: () => api.get('/methods/categories'),
  list: (params?: { category_id?: string; difficulty?: string; search?: string; sort?: string }) =>
    api.get('/methods', { params }),
  get: (id: string) => api.get(`/methods/${id}`),
  recent: (limit?: number) => api.get('/methods/recent', { params: { limit } }),
  stats: () => api.get('/methods/stats'),
};

export const bundlesApi = {
  list: () => api.get('/methods/bundles'),
  get: (id: string) => api.get(`/methods/bundles/${id}`),
};

export const methodPurchaseApi = {
  createCheckout: (bundleId: string) => api.post(`/methods/bundles/${bundleId}/checkout`),
  getCredits: () => api.get('/methods/credits'),
  getUnlocked: () => api.get('/methods/unlocked'),
  unlock: (methodId: string) => api.post(`/methods/${methodId}/unlock`),
  redeemCode: (code: string) => api.post('/methods/redeem-code', { code }),
};

export const customMethodsApi = {
  list: () => api.get('/methods/custom-templates'),
  get: (id: string) => api.get(`/methods/custom-templates/${id}`),
  create: (data: any) => api.post('/methods/custom-templates', data),
  update: (id: string, data: any) => api.put(`/methods/custom-templates/${id}`, data),
  delete: (id: string) => api.delete(`/methods/custom-templates/${id}`),
};

// --- Invoices ---
export const invoicesApi = {
  list: (params?: { status?: string; client_id?: string; package_id?: string; include_cancelled?: boolean; search?: string; date_from?: string; date_to?: string; sort_by?: string; sort_order?: string; skip?: number; limit?: number }) =>
    api.get('/invoices', { params }),
  get: (id: string) => api.get(`/invoices/${id}`),
  create: (data: any) => api.post('/invoices', data),
  update: (id: string, data: any) => api.put(`/invoices/${id}`, data),
  send: (id: string) => api.post(`/invoices/${id}/send`),
  sendReminder: (id: string) => api.post(`/invoices/${id}/send-reminder`),
  delete: (id: string) => api.delete(`/invoices/${id}`),
  cancel: (id: string, data?: { reason?: string }) => api.post(`/invoices/${id}/cancel`, data || {}),
  fromPackage: (data: { client_package_id: string; billing_trigger?: string }) =>
    api.post('/invoices/from-package', data),
  stats: () => api.get('/invoices/stats'),
  overdue: () => api.get('/invoices/overdue'),
  unbilledSessions: (clientId?: string) =>
    api.get('/invoices/unbilled-sessions', { params: clientId ? { client_id: clientId } : undefined }),
  addPayment: (id: string, data: any) => api.post(`/invoices/${id}/payments`, data),
  listPayments: (id: string) => api.get(`/invoices/${id}/payments`),
  audit: (id: string) => api.get(`/invoices/${id}/audit`),
  downloadPdf: (id: string, zugferd: boolean = true) =>
    api.get(`/invoices/${id}/pdf`, { params: { zugferd }, responseType: 'blob' }),
  exportDatev: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/invoices/export/datev', { params, responseType: 'blob' }),
  exportCsv: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/invoices/export/csv', { params, responseType: 'blob' }),
  // Mahnwesen v2
  sendDunning: (id: string, data: { level: string; send_email?: boolean; custom_message?: string; deadline?: string; fee?: number }) =>
    api.post(`/invoices/${id}/dunning`, data),
  dunningHistory: (id: string) => api.get(`/invoices/${id}/dunning-history`),
  checkOverdue: () => api.post('/invoices/check-overdue'),
};

// --- Payments (Stripe) ---
export const paymentsApi = {
  createIntent: (invoiceId: string) =>
    api.post('/payments/create-intent', { invoice_id: invoiceId }),
  getStatus: (invoiceId: string) =>
    api.get(`/payments/${invoiceId}/status`),
  refund: (paymentId: string, amountEur?: number) =>
    api.post('/payments/refund', { payment_id: paymentId, amount_eur: amountEur }),
};

// --- Offers ---
export const offersApi = {
  list: (params?: { status?: string; client_id?: string }) =>
    api.get('/offers', { params }),
  get: (id: string) => api.get(`/offers/${id}`),
  create: (data: any) => api.post('/offers', data),
  update: (id: string, data: any) => api.put(`/offers/${id}`, data),
  delete: (id: string) => api.delete(`/offers/${id}`),
  send: (id: string) => api.post(`/offers/${id}/send`),
  convert: (id: string) => api.post(`/offers/${id}/convert`),
  changeStatus: (id: string, status: string) =>
    api.patch(`/offers/${id}/status`, null, { params: { new_status: status } }),
  exportCsv: (params?: { from_date?: string; to_date?: string; status?: string }) =>
    api.get('/offers/export/csv', { params, responseType: 'blob' }),
  downloadPdf: (id: string) =>
    api.get(`/offers/${id}/pdf`, { responseType: 'blob' }),
};

// --- Process (Client Detail) ---
export const processApi = {
  clientDetail: (clientId: string) => api.get(`/clients/${clientId}/process`),
  addBlock: (caseId: string, data: any) => api.post(`/cases/${caseId}/blocks`, data),
  updateBlock: (blockId: string, data: any) => api.put(`/blocks/${blockId}`, data),
  deleteBlock: (blockId: string) => api.delete(`/blocks/${blockId}`),
  exportBlockHtml: (blockId: string) => api.get(`/blocks/${blockId}/export/html`, { responseType: 'text' }),
  shareBlock: (blockId: string, data: { client_email: string }) => api.post(`/blocks/${blockId}/share`, data),
  sendFillableForm: (blockId: string, data: { client_email: string; client_name?: string }) =>
    api.post(`/blocks/${blockId}/send-fillable`, data),
  previewFillable: (blockId: string) => api.get(`/blocks/${blockId}/preview-fillable`, { responseType: 'text' }),
  reorderBlocks: (caseId: string, order: string[]) => api.put(`/cases/${caseId}/reorder-blocks`, { order }),
  linkSession: (blockId: string, data: { session_id?: string; booking_id?: string; new_session_date?: string }) =>
    api.post(`/blocks/${blockId}/link-session`, data),
  unlinkedSessions: (caseId: string) => api.get('/sessions/unlinked', { params: { case_id: caseId } }),
  unlinkedBookings: (clientId: string) => api.get('/bookings/unlinked', { params: { client_id: clientId } }),
};

// --- Documents ---
export const documentsApi = {
  list: (params?: { client_id?: string }) => api.get('/documents', { params }),
  download: (id: string) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  delete: (id: string) => api.delete(`/documents/${id}`),
  upload: (data: { client_id: string; html_content: string; title: string; document_type?: string; file_name?: string }) =>
    api.post('/documents/upload', data),
  uploadFile: (file: File, clientId: string, title?: string, documentType?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', clientId);
    formData.append('title', title || file.name.replace(/\.[^.]+$/, ''));
    formData.append('document_type', documentType || 'contract');
    return api.post('/documents/upload-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// --- Contract Templates ---
export const contractTemplatesApi = {
  list: () => api.get('/contract-templates'),
  getDefault: () => api.get('/contract-templates/default'),
  get: (id: string) => api.get(`/contract-templates/${id}`),
  create: (data: { name: string; description?: string; sections: string; is_default?: boolean }) =>
    api.post('/contract-templates', data),
  update: (id: string, data: { name?: string; description?: string; sections?: string; is_default?: boolean }) =>
    api.put(`/contract-templates/${id}`, data),
  delete: (id: string) => api.delete(`/contract-templates/${id}`),
  duplicate: (id: string) => api.post(`/contract-templates/${id}/duplicate`),
  setDefault: (id: string) => api.post(`/contract-templates/${id}/set-default`),
};

// --- Bookings ---
export const bookingSlotsApi = {
  list: (params?: { from_date?: string; to_date?: string; available_only?: boolean }) =>
    api.get('/booking-slots', { params }),
  create: (data: any) => api.post('/booking-slots', data),
  createBulk: (data: any[]) => api.post('/booking-slots/bulk', data),
  update: (id: string, data: any) => api.put(`/booking-slots/${id}`, data),
  delete: (id: string) => api.delete(`/booking-slots/${id}`),
};

export const bookingsApi = {
  list: (params?: { status?: string; client_id?: string }) =>
    api.get('/bookings', { params }),
  create: (data: any) => api.post('/bookings', data),
  update: (id: string, data: any) => api.put(`/bookings/${id}`, data),
};

// --- Availability Settings ---
export const availabilityApi = {
  list: () => api.get('/availability-settings'),
  create: (data: any) => api.post('/availability-settings', data),
  update: (id: string, data: any) => api.put(`/availability-settings/${id}`, data),
  delete: (id: string) => api.delete(`/availability-settings/${id}`),
  generateSlots: (data: { from_date: string; to_date: string }) =>
    api.post('/availability-settings/generate-slots', data),
  getSlug: () => api.get('/booking-slug'),
  updateSlug: (slug: string) => api.put('/booking-slug', { slug }),
};

// --- Public Booking (no auth) ---
const publicApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

export const publicBookingApi = {
  getCoachInfo: (slug: string) => publicApi.get(`/buchen/${slug}`),
  getSlots: (slug: string) => publicApi.get(`/buchen/${slug}/slots`),
  book: (slug: string, data: { booking_slot_id: string; guest_name: string; guest_email: string; guest_message?: string }) =>
    publicApi.post(`/buchen/${slug}/book`, data),
};

// --- Portal (Coach-side) ---
export const portalApi = {
  invite: (data: { client_id: string; email?: string }) => api.post('/portal/invite', data),
  getClientStatus: (clientId: string) => api.get(`/portal/clients/${clientId}/status`),
  updateSettings: (clientId: string, settings: any) => api.put(`/portal/clients/${clientId}/settings`, settings),
  activatePortal: (clientId: string) => api.post(`/portal/clients/${clientId}/activate`),
  deactivatePortal: (clientId: string) => api.post(`/portal/clients/${clientId}/deactivate`),
};

// --- Portal (Client-side) — separate axios instance with portal_token ---
const portalApi2 = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

portalApi2.interceptors.request.use((config) => {
  const token = localStorage.getItem('portal_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

portalApi2.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !err.config.url?.includes('/auth/')) {
      localStorage.removeItem('portal_token');
      window.location.replace('/portal/login');
    }
    return Promise.reject(err);
  }
);

export const portalClientApi = {
  login: (data: { email: string; password: string }) => portalApi2.post('/portal/auth/login', data),
  requestCode: (email: string) => portalApi2.post('/portal/auth/request-code', { email }),
  verifyCode: (email: string, code: string) => portalApi2.post('/portal/auth/verify-code', { email, code }),
  register: (data: { token: string; password: string }) => portalApi2.post('/portal/auth/register', data),
  validateCode: (token: string) => portalApi2.post('/portal/auth/validate-code', { token }),
  resendInvitation: (email: string) => portalApi2.post('/portal/auth/resend-invitation', { email }),
  forgotPassword: (email: string) => portalApi2.post('/portal/auth/forgot-password', { email }),
  resetPassword: (data: { email: string; code: string; new_password: string }) =>
    portalApi2.post('/portal/auth/reset-password', data),
  profile: () => portalApi2.get('/portal/profile'),
  updateProfile: (data: any) => portalApi2.put('/portal/profile', data),
  invoices: () => portalApi2.get('/portal/invoices'),
  invoicePdf: (id: string) => portalApi2.get(`/portal/invoices/${id}/pdf`, { responseType: 'blob' }),
  slots: () => portalApi2.get('/portal/slots'),
  bookings: () => portalApi2.get('/portal/bookings'),
  createBooking: (data: { booking_slot_id: string; notes?: string }) => portalApi2.post('/portal/bookings', data),
  sessions: () => portalApi2.get('/portal/sessions'),
  documents: () => portalApi2.get('/portal/documents'),
  downloadDocument: (id: string) => portalApi2.get(`/portal/documents/${id}/download`, { responseType: 'blob' }),
  homework: () => portalApi2.get('/portal/homework'),
  submitHomework: (sessionId: string, data: { response_text: string }) =>
    portalApi2.post(`/portal/homework/${sessionId}/submit`, data),
  followups: () => portalApi2.get('/portal/followups'),
  cancelSession: (id: string) => portalApi2.post(`/portal/sessions/${id}/cancel`),
  createPaymentIntent: (invoiceId: string) => portalApi2.post(`/portal/invoices/${invoiceId}/pay`),
  // Support / Hilfe
  supportTickets: () => portalApi2.get('/portal/support'),
  supportTicketDetail: (id: string) => portalApi2.get(`/portal/support/${id}`),
  createSupportTicket: (data: { subject: string; message: string; category?: string }) => portalApi2.post('/portal/support', data),
  replySupportTicket: (id: string, data: { message: string }) => portalApi2.post(`/portal/support/${id}/reply`, data),
  // Änderungsanträge
  changeRequests: () => portalApi2.get('/portal/change-requests'),
  createChangeRequest: (data: { field_name: string; new_value: string; reason?: string }) =>
    portalApi2.post('/portal/change-requests', data),
  // Chat
  chatMessages: (params?: { skip?: number; limit?: number }) =>
    portalApi2.get('/portal/chat/messages', { params }),
  sendChatMessage: (data: { content: string }) =>
    portalApi2.post('/portal/chat/messages', data),
};

// --- Coach: Support ---
export const supportApi = {
  tickets: (params?: { status?: string }) => api.get('/support/tickets', { params }),
  ticketDetail: (id: string) => api.get(`/support/tickets/${id}`),
  createTicket: (data: { subject: string; message: string; category?: string; priority?: string }) =>
    api.post('/support/tickets', data),
  replyTicket: (id: string, data: { message: string }) => api.post(`/support/tickets/${id}/reply`, data),
  updateTicketStatus: (id: string, data: { status: string }) => api.patch(`/support/tickets/${id}/status`, data),
  clientTickets: (params?: { status?: string }) => api.get('/support/client-tickets', { params }),
  clientTicketDetail: (id: string) => api.get(`/support/client-tickets/${id}`),
  replyClientTicket: (id: string, data: { message: string }) => api.post(`/support/client-tickets/${id}/reply`, data),
  updateClientTicketStatus: (id: string, data: { status: string }) => api.patch(`/support/client-tickets/${id}/status`, data),
};

// --- Coach: Änderungsanträge ---
export const changeRequestApi = {
  list: (params?: { status?: string }) => api.get('/change-requests', { params }),
  count: () => api.get('/change-requests/count'),
  detail: (id: string) => api.get(`/change-requests/${id}`),
  approve: (id: string) => api.post(`/change-requests/${id}/approve`),
  reject: (id: string, data: { comment?: string }) => api.post(`/change-requests/${id}/reject`, data),
};

// --- Privacy/DSGVO ---
export const privacyApi = {
  exportCoachData: () => api.get('/privacy/export', { responseType: 'blob' }),
  exportClientData: (clientId: string) => api.get(`/privacy/client-export/${clientId}`, { responseType: 'blob' }),
  deleteClient: (clientId: string) => api.delete(`/privacy/client-delete/${clientId}`),
  deleteAccount: () => api.delete('/privacy/delete-account'),
};

// --- Calendar ---
export const calendarApi = {
  events: (params?: { from_date?: string; to_date?: string; event_type?: string }) =>
    api.get('/calendar/events', { params }),
  exportIcs: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/calendar/export/ics', { params, responseType: 'blob' }),
};

// --- Integrations ---
export const integrationsApi = {
  list: () => api.get('/integrations'),
  outlookConnect: (params: { client_id: string; redirect_uri: string }) =>
    api.get('/integrations/outlook/connect', { params }),
  outlookCallback: (data: { code: string; client_id: string; client_secret: string; redirect_uri: string }) =>
    api.post('/integrations/outlook/callback', null, { params: data }),
  googleConnect: (params: { client_id: string; redirect_uri: string }) =>
    api.get('/integrations/google/connect', { params }),
  googleCallback: (data: { code: string; client_id: string; client_secret: string; redirect_uri: string }) =>
    api.post('/integrations/google/callback', data),
  sync: (id: string) => api.post(`/integrations/sync/${id}`),
  disconnect: (id: string) => api.delete(`/integrations/${id}`),
  getIcalFeed: () => api.get('/integrations/ical-feed'),
  // Zoom
  zoomConnect: (params: { account_id: string; client_id: string; client_secret: string }) =>
    api.post('/integrations/zoom/connect', null, { params }),
  zoomStatus: () => api.get('/integrations/zoom/status'),
  zoomUpdateSettings: (params: { auto_create_meetings: boolean }) =>
    api.patch('/integrations/zoom/settings', null, { params }),
  // Google
  googleStatus: () => api.get('/integrations/google/status'),
  googleUpdateSettings: (params: {
    auto_sync?: boolean;
    invite_clients?: boolean;
    auto_upload_invoices?: boolean;
    auto_upload_offers?: boolean;
    auto_upload_contracts?: boolean;
    auto_upload_documents?: boolean;
    auto_upload_session_notes?: boolean;
    drive_target_folder_id?: string;
    drive_target_folder_name?: string;
    drive_folder_category?: string;
    drive_folder_id?: string;
  }) => api.patch('/integrations/google/settings', null, { params }),
  // Google Drive
  googleDriveFiles: (parentId?: string) =>
    api.get('/integrations/google-drive/files', { params: { parent_id: parentId } }),
  googleDriveFolders: (search?: string) => api.get('/integrations/google-drive/folders', { params: { search } }),
  googleDriveLink: (fileId: string, clientId?: string) =>
    api.post('/integrations/google-drive/link', null, { params: { file_id: fileId, client_id: clientId } }),
  googleDriveImport: (fileId: string, clientId?: string) =>
    api.post(`/integrations/google-drive/import/${fileId}`, null, { params: { client_id: clientId } }),
  googleDriveUpload: (file: File, folderId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/integrations/google-drive/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { folder_id: folderId },
    });
  },
};

// --- Reminders & Notifications ---
export const remindersApi = {
  check: () => api.post('/reminders/check'),
  checkAppointments: () => api.post('/reminders/check-appointments'),
  notifications: (params?: { unread_only?: boolean }) => api.get('/reminders/notifications', { params }),
  markRead: (id: string) => api.post(`/reminders/notifications/${id}/read`),
  markAllRead: () => api.post('/reminders/notifications/read-all'),
};

// --- Dashboard ---
export const dashboardApi = {
  stats: () => api.get('/dashboard/stats'),
  chartData: (months?: number) => api.get('/dashboard/chart-data', { params: months ? { months } : undefined }),
};

// --- Reports ---
export const reportsApi = {
  // Legacy endpoints (still work)
  revenue: (params?: any) => api.get('/reports/revenue', { params }),
  sessions: (params?: any) => api.get('/reports/sessions', { params }),
  clients: (params?: any) => api.get('/reports/clients', { params }),
  // v2 endpoints
  financials: (params?: any) => api.get('/reports/financials', { params }),
  revenueChart: (params?: any) => api.get('/reports/revenue-chart', { params }),
  revenueByType: (params?: any) => api.get('/reports/revenue-by-type', { params }),
  insights: (params?: any) => api.get('/reports/insights', { params }),
  invoices: (params?: any) => api.get('/reports/invoices', { params }),
  clientsRanking: (params?: any) => api.get('/reports/clients-ranking', { params }),
  clientsStats: (params?: any) => api.get('/reports/clients-stats', { params }),
  taxOverview: () => api.get('/reports/tax-overview'),
  // Exports
  exportCsv: (params?: any) => api.get('/reports/export/csv', { params, responseType: 'blob' }),
  exportDatev: (params?: any) => api.get('/reports/export/datev', { params, responseType: 'blob' }),
  exportPdf: (params?: any) => api.get('/reports/export/pdf', { params, responseType: 'blob' }),
  exportEur: (params?: any) => api.get('/reports/export/eur', { params, responseType: 'blob' }),
  exportClients: () => api.get('/reports/export/clients', { responseType: 'blob' }),
  // Practice-wide dashboard stats (owner/admin only)
  practiceStats: () => api.get('/reports/practice-stats'),
};

// --- Follow-Up ---
export const followupApi = {
  templates: () => api.get('/followup/templates'),
  listForCase: (caseId: string) => api.get(`/followup/case/${caseId}`),
  create: (caseId: string, data: any) => api.post(`/followup/case/${caseId}`, data),
  send: (instanceId: string) => api.post(`/followup/${instanceId}/send`),
  getResponse: (instanceId: string) => api.get(`/followup/${instanceId}/response`),
};

// --- Email Templates ---
export const emailTemplatesApi = {
  list: () => api.get('/email-templates'),
  get: (key: string) => api.get(`/email-templates/${key}`),
  update: (key: string, data: { subject: string; body: string }) =>
    api.patch(`/email-templates/${key}`, data),
  reset: (key: string) => api.post(`/email-templates/${key}/reset`),
  preview: (key: string) => api.post(`/email-templates/${key}/preview`),
};

// --- Webhooks ---
export const webhooksApi = {
  events: () => api.get('/webhooks/events'),
  list: () => api.get('/webhooks'),
  get: (id: string) => api.get(`/webhooks/${id}`),
  create: (data: { name: string; url: string; events: string[]; secret?: string }) =>
    api.post('/webhooks', data),
  update: (id: string, data: { name?: string; url?: string; events?: string[]; secret?: string; is_active?: boolean }) =>
    api.put(`/webhooks/${id}`, data),
  delete: (id: string) => api.delete(`/webhooks/${id}`),
  test: (id: string) => api.post(`/webhooks/${id}/test`),
  toggle: (id: string) => api.post(`/webhooks/${id}/toggle`),
  logs: (id: string) => api.get(`/webhooks/${id}/logs`),
  retry: (logId: string) => api.post(`/webhooks/logs/${logId}/retry`),
};

// --- Quality Management (ISO 9001) ---
export const qmApi = {
  dashboard: () => api.get('/qm/dashboard'),
  // Policy
  getPolicy: () => api.get('/qm/policy'),
  savePolicy: (data: { vision?: string; mission?: string; values?: string; policy_text?: string; approved_by?: string }) =>
    api.put('/qm/policy', data),
  approvePolicy: () => api.post('/qm/policy/approve'),
  // Objectives
  listObjectives: (params?: { status?: string }) => api.get('/qm/objectives', { params }),
  createObjective: (data: any) => api.post('/qm/objectives', data),
  updateObjective: (id: string, data: any) => api.put(`/qm/objectives/${id}`, data),
  deleteObjective: (id: string) => api.delete(`/qm/objectives/${id}`),
  // Risks
  listRisks: (params?: { status?: string; risk_type?: string }) => api.get('/qm/risks', { params }),
  createRisk: (data: any) => api.post('/qm/risks', data),
  updateRisk: (id: string, data: any) => api.put(`/qm/risks/${id}`, data),
  deleteRisk: (id: string) => api.delete(`/qm/risks/${id}`),
  // Documents
  listDocuments: (params?: { status?: string; category?: string }) => api.get('/qm/documents', { params }),
  createDocument: (data: any) => api.post('/qm/documents', data),
  getDocument: (id: string) => api.get(`/qm/documents/${id}`),
  updateDocument: (id: string, data: any) => api.put(`/qm/documents/${id}`, data),
  deleteDocument: (id: string) => api.delete(`/qm/documents/${id}`),
  downloadDocumentPdf: (id: string) => api.get(`/qm/documents/${id}/pdf`, { responseType: 'blob' }),
  // Nonconformities
  listNCs: (params?: { status?: string; nc_type?: string }) => api.get('/qm/nonconformities', { params }),
  createNC: (data: any) => api.post('/qm/nonconformities', data),
  updateNC: (id: string, data: any) => api.put(`/qm/nonconformities/${id}`, data),
  deleteNC: (id: string) => api.delete(`/qm/nonconformities/${id}`),
  // Audits
  listAudits: (params?: { status?: string }) => api.get('/qm/audits', { params }),
  createAudit: (data: any) => api.post('/qm/audits', data),
  updateAudit: (id: string, data: any) => api.put(`/qm/audits/${id}`, data),
  deleteAudit: (id: string) => api.delete(`/qm/audits/${id}`),
  // Records (Ablage)
  listRecords: (params?: { template_type?: string; status?: string }) => api.get('/qm/records', { params }),
  createRecord: (data: any) => api.post('/qm/records', data),
  getRecord: (id: string) => api.get(`/qm/records/${id}`),
  updateRecord: (id: string, data: any) => api.put(`/qm/records/${id}`, data),
  deleteRecord: (id: string) => api.delete(`/qm/records/${id}`),
  nextRecordNumber: (type: string) => api.get(`/qm/records/next-number/${type}`),
  downloadRecordPdf: (id: string) => api.get(`/qm/records/${id}/pdf`, { responseType: 'blob' }),
  // Management Reviews
  listReviews: () => api.get('/qm/reviews'),
  createReview: (data: any) => api.post('/qm/reviews', data),
  getReview: (id: string) => api.get(`/qm/reviews/${id}`),
  updateReview: (id: string, data: any) => api.put(`/qm/reviews/${id}`, data),
  deleteReview: (id: string) => api.delete(`/qm/reviews/${id}`),
};

// --- Legal / DSGVO ---
export const publicLegalApi = {
  getText: (type: string, coachId?: string) =>
    publicApi.get('/legal/text/' + type, { params: coachId ? { coach_id: coachId } : undefined }),
};

export const legalApi = {
  getTexts: () => api.get('/legal/texts'),
  updateText: (type: string, data: { title?: string; content?: string }) =>
    api.put(`/legal/texts/${type}`, data),
  signAvv: () => api.post('/legal/avv/sign'),
  getAvvStatus: () => api.get('/legal/avv/status'),
  downloadAvv: () => api.get('/legal/avv/download', { responseType: 'blob' }),
  updateConsent: (data: { consent_terms?: boolean; consent_privacy?: boolean; consent_newsletter?: boolean }) =>
    api.put('/legal/consent', data),
};

// --- Account (Mein Konto) ---
export const accountApi = {
  profile: () => api.get('/account/profile'),
  updateProfile: (data: any) => api.patch('/account/profile', data),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/account/change-password', data),
  subscription: () => api.get('/account/subscription'),
  getTheme: () => api.get('/account/theme'),
  updateTheme: (theme: string) => api.patch('/account/theme', { theme }),
};

// --- Admin ---
export const adminApi = {
  dashboard: () => api.get('/admin/dashboard'),
  users: (params?: { search?: string; status?: string; skip?: number; limit?: number }) =>
    api.get('/admin/users', { params }),
  userDetail: (id: string) => api.get(`/admin/users/${id}`),
  updateUserStatus: (id: string, is_active: boolean) =>
    api.patch(`/admin/users/${id}/status`, { is_active }),
  updateUserRole: (id: string, role: string) =>
    api.patch(`/admin/users/${id}/role`, { role }),
  createUser: (data: { email: string; password: string; first_name: string; last_name: string; role?: string }) =>
    api.post('/admin/users', data),
  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
  resetUserPassword: (userId: string) =>
    api.post(`/admin/users/${userId}/reset-password`),
  subscriptions: () => api.get('/admin/subscriptions'),
  updateSubscription: (id: string, data: { plan_type?: string; status?: string }) =>
    api.patch(`/admin/subscriptions/${id}`, data),
  grantFreeAccess: (userId: string) =>
    api.post('/admin/subscriptions/free-access', null, { params: { user_id: userId } }),
  grantBeta: (userId: string, weeks: number = 4) =>
    api.post('/admin/subscriptions/beta', { user_id: userId, weeks }),
  revokeBeta: (userId: string) =>
    api.post('/admin/subscriptions/revoke-beta', null, { params: { user_id: userId } }),
  systemStatus: () => api.get('/admin/system/status'),
  systemLogs: (params?: { level?: string; limit?: number }) =>
    api.get('/admin/system/logs', { params }),
  supportTickets: (params?: { status?: string }) =>
    api.get('/admin/support', { params }),
  updateTicket: (id: string, data: { status?: string; priority?: string; internal_notes?: string }) =>
    api.patch(`/admin/support/${id}`, data),
  ticketDetail: (id: string) => api.get(`/admin/support/${id}`),
  replyTicket: (id: string, data: { message: string }) => api.post(`/admin/support/${id}/reply`, data),
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data: { maintenance_mode?: boolean; maintenance_message?: string; max_coaches?: number; storage_limit_gb?: number }) =>
    api.put('/admin/settings', data),
  toggleMaintenance: (data: { enabled: boolean; message?: string }) =>
    api.post('/admin/maintenance', data),
  // Audit Logs
  auditLogs: (params?: { skip?: number; limit?: number; user_id?: string; action?: string; entity_type?: string; from_date?: string; to_date?: string }) =>
    api.get('/admin/audit-logs', { params }),
  auditLogsExport: (params?: { user_id?: string; action?: string; entity_type?: string; from_date?: string; to_date?: string }) =>
    api.get('/admin/audit-logs/export', { params, responseType: 'blob' }),
  // Maintenance
  health: () => api.get('/admin/health'),
  healthHistory: (hours?: number) => api.get('/admin/health/history', { params: { hours: hours || 24 } }),
  jobs: () => api.get('/admin/jobs'),
  runJob: (jobId: string) => api.post(`/admin/jobs/${jobId}/run`),
  hygieneReport: () => api.get('/admin/hygiene/report'),
  runCleanup: () => api.post('/admin/hygiene/cleanup'),
  maintenanceLogs: (params?: { job_name?: string; status?: string; limit?: number }) =>
    api.get('/admin/maintenance-logs', { params }),
  errors: (params?: { status?: string; level?: string; limit?: number }) =>
    api.get('/admin/errors', { params }),
  errorStats: () => api.get('/admin/errors/stats'),
  errorDetail: (groupId: string) => api.get(`/admin/errors/${groupId}`),
  resolveError: (groupId: string) => api.post(`/admin/errors/${groupId}/resolve`),
  ignoreError: (groupId: string) => api.post(`/admin/errors/${groupId}/ignore`),
  backups: (params?: { limit?: number }) => api.get('/admin/backups', { params }),
  createBackup: () => api.post('/admin/backups/create'),
  verifyBackup: (id: string) => api.post(`/admin/backups/${id}/verify`),
  // Business Intelligence
  bi: (params?: { period?: string }) => api.get('/admin/bi', { params }),
  // Incidents
  incidents: (params?: { status?: string; limit?: number }) =>
    api.get('/admin/incidents', { params }),
  createIncident: (data: { title: string; description?: string; severity: string; affected_services?: string; is_public?: boolean }) =>
    api.post('/admin/incidents', data),
  incidentDetail: (id: string) => api.get(`/admin/incidents/${id}`),
  updateIncident: (id: string, data: { status?: string; root_cause?: string; post_mortem?: string }) =>
    api.put(`/admin/incidents/${id}`, data),
  addIncidentUpdate: (id: string, data: { status: string; message: string }) =>
    api.post(`/admin/incidents/${id}/update`, data),
  // Changelog
  changelogEntries: () => api.get('/admin/changelog'),
  createChangelog: (data: { version: string; title: string; items: any[]; is_published?: boolean }) =>
    api.post('/admin/changelog', data),
  updateChangelog: (id: string, data: any) => api.put(`/admin/changelog/${id}`, data),
  deleteChangelog: (id: string) => api.delete(`/admin/changelog/${id}`),
  // Announcements
  announcements: () => api.get('/admin/announcements'),
  createAnnouncement: (data: { title: string; message: string; announcement_type: string; channels: any; target: string }) =>
    api.post('/admin/announcements', data),
  activeAnnouncement: () => api.get('/admin/announcements/active'),
  dismissAnnouncement: (id: string) => api.post(`/admin/announcements/${id}/dismiss`),
};

// --- Subscriptions ---
export const subscriptionsApi = {
  me: () => api.get('/subscriptions/me'),
  usage: () => api.get('/subscriptions/usage'),
  upgrade: (plan: string) => api.post('/subscriptions/upgrade', { plan }),
  cancel: () => api.post('/subscriptions/cancel'),
  tiers: () => api.get('/subscriptions/tiers'),
  completeOnboarding: (data: { onboarding_completed?: boolean; first_name?: string; last_name?: string; business_name?: string }) =>
    api.patch('/subscriptions/onboarding', data),
};

// ── Demo Data ─────────────────────────────────────────────
export const demoApi = {
  seed: () => api.post('/demo/seed'),
  clear: () => api.delete('/demo/clear'),
};

// --- Chat (Unified Messaging) ---
export const chatApi = {
  // Conversations
  conversations: (params?: { search?: string; skip?: number; limit?: number }) =>
    api.get('/chat/conversations', { params }),
  startConversation: (clientId: string) =>
    api.get(`/chat/conversations/start/${clientId}`),
  unreadCount: () => api.get('/chat/unread-count'),
  markRead: (conversationId: string) =>
    api.patch(`/chat/conversations/${conversationId}/read`),
  changeChannel: (conversationId: string, data: { preferred_channel: string }) =>
    api.patch(`/chat/conversations/${conversationId}/channel`, data),
  // Messages
  messages: (conversationId: string, params?: { skip?: number; limit?: number }) =>
    api.get(`/chat/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId: string, data: { content: string; content_type?: string }) =>
    api.post(`/chat/conversations/${conversationId}/messages`, data),
  deleteMessage: (messageId: string) =>
    api.delete(`/chat/messages/${messageId}`),
  // Archive
  archived: (params?: { skip?: number; limit?: number }) =>
    api.get('/chat/archived', { params }),
  archive: (conversationId: string, data?: { reason?: string }) =>
    api.patch(`/chat/conversations/${conversationId}/archive`, data || {}),
  unarchive: (conversationId: string) =>
    api.patch(`/chat/conversations/${conversationId}/unarchive`),
  // Archive Settings
  archiveSettings: () => api.get('/chat/archive-settings'),
  updateArchiveSettings: (data: any) => api.put('/chat/archive-settings', data),
  // Auto-Replies
  autoReplies: () => api.get('/chat/auto-replies'),
  updateAutoReply: (ruleType: string, data: any) =>
    api.put(`/chat/auto-replies/${ruleType}`, data),
  toggleAutoReply: (ruleType: string) =>
    api.patch(`/chat/auto-replies/${ruleType}/toggle`),
};

export const channelsApi = {
  configs: () => api.get('/channels/configs'),
  upsertConfig: (channel: string, data: any) =>
    api.put(`/channels/configs/${channel}`, data),
  testConfig: (channel: string) =>
    api.post(`/channels/configs/${channel}/test`),
};

// --- Team / Practice ---
export const practiceApi = {
  getInviteInfo: (token: string) => api.get(`/practices/invite-info/${token}`),
  me: () => api.get('/practices/me'),
  create: (data: { name: string; description?: string }) => api.post('/practices', data),
  update: (data: { name?: string; description?: string; phone?: string; email?: string; website?: string }) =>
    api.patch('/practices', data),
  members: () => api.get('/practices/members'),
  invitations: () => api.get('/practices/invitations'),
  invite: (data: { email: string; role: string; permission_create_clients?: boolean; permission_manage_members?: boolean; permission_view_reports?: boolean; permission_manage_invoices?: boolean }) =>
    api.post('/practices/invite', data),
  cancelInvitation: (id: string) => api.delete(`/practices/invitations/${id}`),
  updateMember: (userId: string, data: { role?: string; permission_create_clients?: boolean; permission_manage_members?: boolean; permission_view_reports?: boolean; permission_manage_invoices?: boolean }) =>
    api.patch(`/practices/members/${userId}`, data),
  removeMember: (userId: string) => api.delete(`/practices/members/${userId}`),
  acceptInvite: (token: string) => api.post('/practices/accept-invite', { token }),
};

export default api;
